import logger from 'redux-logger';
import { createStore, applyMiddleware, combineReducers, compose, Reducer } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { translateService } from './services/TranslateService';
import SecureLS from 'secure-ls';
import { ResponseModalStruct } from './interfaces/ModalStructs';
import ApiService from './services/api/ApiService';
import * as Sentry from "@sentry/react";
import { ICoupon  } from './interfaces/VehicleSearch';

// structure of entire Redux store
export interface GlobalStore {
    chauffeurReducer: {
        dbToken: string | undefined;
        jwt: string | undefined;
        deviceLanguage: string;
        API_VERSION: string;
        API_URL: string;
        responseModal: ResponseModalStruct | undefined;
        apiMessage: string | undefined;
        apiService: ApiService | undefined;
        coupon: ICoupon | undefined,
    };
}

// each of the actions we use
type ActionApiUrl = {
    type: 'SET_API_URL';
    payload: string;
};
type ActionApiVer = {
    type: 'SET_API_VERSION';
    payload: string;
};
type ActionLang = {
    type: 'SET_LANG';
    payload: string;
};
type ActionToken = {
    type: 'SET_TOKEN';
    payload: {
        token: string;
        jwt: string;
        driverLogin: boolean;
    };
};
type ActionClearToken = {
    type: 'CLEAR_TOKEN';
};
type ActionGetToken = {
    type: 'GET_TOKEN';
};
type ActionResModal = {
    type: 'SET_RESPONSE_MODAL';
    payload: any;
};
type ActionApiMessage = {
    type: 'SET_API_MESSAGE';
    payload: string | undefined;
};
type ActionSetApiService = {
    type: 'SET_API_SERVICE';
    payload: ApiService;
};

type ActionSetCoupon = {
    type: 'SET_DISCOUNT_COUPON';
    payload: ICoupon ;
};

type ActionSetClearSelections = {
    type: 'CLEAR_SELECTIONS';
    payload: any ;
};

// all the Actions merged, and exported as Dispatch
type Action =
    | ActionApiUrl
    | ActionApiVer
    | ActionLang
    | ActionToken
    | ActionGetToken
    | ActionClearToken
    | ActionResModal
    | ActionApiMessage
    | ActionSetApiService
    | ActionSetCoupon
    | ActionSetClearSelections

export type Dispatch = (action: Action) => void;

const aesC = new SecureLS({ encodingType: 'aes', encryptionSecret: process.env.REACT_APP_TSEC });

// initial state of the store
const INITIAL_STATE: GlobalStore = {
    chauffeurReducer: {
        dbToken: '',
        jwt: '',
        deviceLanguage: 'fi',
        API_VERSION: '2',
        API_URL: '',
        responseModal: undefined,
        apiMessage: undefined,
        apiService: undefined,
        coupon: undefined,
    },
};

const mainReducer: Reducer = (state = INITIAL_STATE.chauffeurReducer, action: Action) => {
    switch (action.type) {
        case 'SET_API_VERSION':
            return {
                ...state,
                API_VERSION: action.payload,
            };

        case 'SET_API_URL':
            return {
                ...state,
                API_URL: action.payload,
            };

        case 'SET_LANG':
            // is this a side-effect??
            translateService.setLocale(action.payload);
            return {
                ...state,
                deviceLanguage: action.payload,
            };

        case 'CLEAR_TOKEN':
            aesC.remove('C2-XT');
            aesC.remove('C2-STORE');
            return {
                ...state,
                dbToken: INITIAL_STATE.chauffeurReducer.dbToken,
                jwt: INITIAL_STATE.chauffeurReducer.jwt,
            };

        case 'SET_TOKEN':
            aesC.set('C2-XT', action.payload.token);
            aesC.set('C2-STORE', action.payload.jwt);
            localStorage.setItem('C2-DL', action.payload.driverLogin === true ? '1' : '0');
            return {
                ...state,
                dbToken: action.payload.token,
                jwt: action.payload.jwt,
                driverLogin: action.payload.driverLogin,
            };

        case 'GET_TOKEN':{
            try {
                return {
                    ...state,
                    dbToken: aesC.get('C2-XT'),
                    jwt: aesC.get('C2-STORE'),
                };
            } catch(e) {                
                console.log(e);                
                aesC.remove('C2-XT');
                aesC.remove('C2-STORE');
                return {
                    ...state,
                    dbToken: '',
                    jwt: '',
                }
            }
        }

        case 'SET_RESPONSE_MODAL':
            if (action.payload) {
                return {
                    ...state,
                    responseModal: action.payload,
                };
            } else {
                return {
                    ...state,
                    responseModal: undefined,
                };
            }

        case 'SET_API_MESSAGE':
            if (action.payload) {
                return {
                    ...state,
                    apiMessage: action.payload,
                };
            } else {
                return {
                    ...state,
                    apiMessage: undefined,
                };
            }

        case 'SET_API_SERVICE':
            return {
                ...state,
                apiService: action.payload,
            };
    
        case 'SET_DISCOUNT_COUPON':
            sessionStorage.setItem("coupon",JSON.stringify(action.payload));
            return {
                ...state,
                coupon: action.payload,
            };

        case 'CLEAR_SELECTIONS':
            sessionStorage.removeItem("coupon");
            sessionStorage.removeItem("page");
            sessionStorage.removeItem("time");
            sessionStorage.removeItem("reservationCreated");
            return {
                ...state,
                selectedCar: null,
                coupon: undefined
            };

        default:
            return state;
    }
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

// We only want this on localhost
const middleware: any = window.location.hostname.toString().indexOf('24rent.fi') === -1 ? applyMiddleware(logger) : null;

const combinedReducer = combineReducers({
    chauffeurReducer: mainReducer,
});



export const store = createStore(combinedReducer, compose(
    sentryReduxEnhancer, composeWithDevTools(middleware)) as any);
